import { useState } from 'react';

import { formatDateAr } from '../shared/format/dateFormat';

import { composeChildName } from '../features/employees/application/childName';
import {
  EMPTY_DEPENDENT_FORM,
  needsOnlyFirstName,
  type DependentFieldErrors,
  type DependentFormValues,
} from '../features/employees/application/dependentForm';
import {
  Alert,
  Badge,
  Button,
  Card,
  DateField,
  SelectField,
  TextField,
} from '../shared/components';

import authStyles from './ScreenLayout.module.css';
import styles from './DependentsPage.module.css';

/**
 * The employee's dependants: what they have entered, and a form to add more.
 *
 * Eligibility is evaluated by `dependentForm.ts` against the stored policy.
 * This screen shows the verdict; it does not know what the limits are (§5.4).
 */

export interface DependentRow {
  readonly id: string;
  readonly fullName: string;
  /** The stored value, not the label: reopening the form needs what it holds. */
  readonly relation: string;
  readonly relationLabelAr: string;
  readonly gender: string;
  readonly birthDate: string;
  readonly nationalId: string | null;
  readonly statusLabelAr: string;
  readonly statusTone: 'neutral' | 'success' | 'warning' | 'error' | 'info';
  /** False once the committee has decided this one. Per dependant, not per list. */
  readonly editable: boolean;
}

export interface DependentsPageProps {
  readonly dependents: readonly DependentRow[];
  readonly relationOptions: readonly { readonly value: string; readonly labelAr: string }[];
  readonly childrenShortfall: number;
  readonly canAddMore: boolean;
  readonly errors: DependentFieldErrors;
  readonly ineligibleReasonsAr: readonly string[];
  readonly requiresProofDocument: boolean;
  readonly onValidate: (values: DependentFormValues) => void;
  readonly onAdd: (values: DependentFormValues) => Promise<void>;
  /**
   * Saves a change to one already-entered dependant.
   *
   * Per dependant, not per list: an approved child locks while a sibling still
   * awaiting documents stays open. `DependentRow.editable` carries which.
   */
  readonly onUpdate: (id: string, values: DependentFormValues) => Promise<void>;
  readonly errorAr: string | null;
  readonly submissionsClosed: boolean;
  /**
   * What a child's name will be recorded as, from the first name typed so far.
   * Null when it cannot be derived, which is when the full name is asked for.
   */
  readonly derivedChildNameAr: string | null;
  /** The employee's own name, which a child's is derived from and reopened by. */
  readonly employeeFullName: string;
}

/** Kept beside the form so the edit path and the label agree on one answer. */
function isChild(relation: string): boolean {
  return relation === 'son' || relation === 'daughter';
}

export function DependentsPage({
  dependents,
  relationOptions,
  childrenShortfall,
  canAddMore,
  errors,
  ineligibleReasonsAr,
  requiresProofDocument,
  onValidate,
  onAdd,
  onUpdate,
  errorAr,
  submissionsClosed,
  derivedChildNameAr,
  employeeFullName,
}: DependentsPageProps) {
  const [values, setValues] = useState<DependentFormValues>(EMPTY_DEPENDENT_FORM);
  const [saving, setSaving] = useState(false);
  /** The dependant being changed, or null while the form is adding a new one. */
  const [editingId, setEditingId] = useState<string | null>(null);

  const set = (field: keyof DependentFormValues) => (event: { target: { value: string } }) => {
    const next = { ...values, [field]: event.target.value };
    setValues(next);
    // Re-evaluates as they type, so a policy refusal appears before they reach
    // the button rather than after (spec FR-014).
    onValidate(next);
  };

  const setDifferentFather = (differentFather: boolean) => {
    // The field changes meaning — first name to full name — so what was typed
    // under the old meaning is cleared rather than reinterpreted.
    const next = { ...values, differentFather, fullName: '' };
    setValues(next);
    onValidate(next);
  };

  const firstNameOnly = needsOnlyFirstName(values);
  // The toggle is offered for a child whether or not it is currently on —
  // otherwise turning it on would hide the control that turns it off.
  const isChildRelation = isChild(values.relation);

  const startEditing = (dependent: DependentRow) => {
    /*
     * A child reopens in the same shape it was entered in.
     *
     * This used to force the full-name field for every edit, which left the
     * form stuck in that mode: a parent who opened an edit and then went back
     * to adding was asked for the full name of their next child, having been
     * asked only for the first name of the one before.
     *
     * The stored name is derived when the first word plus the employee's
     * lineage reproduces it exactly. When it does not — a half-sibling, or a
     * name entered before this rule existed — the full field is right.
     */
    const [firstWord = ''] = dependent.fullName.trim().split(/\s+/);
    const derived = composeChildName(firstWord, employeeFullName);
    const wasDerived =
      isChild(dependent.relation) && derived?.fullName === dependent.fullName.trim();

    const next: DependentFormValues = {
      relation: dependent.relation,
      fullName: wasDerived ? firstWord : dependent.fullName,
      differentFather: !wasDerived,
      gender: dependent.gender,
      birthDate: dependent.birthDate,
      nationalId: dependent.nationalId ?? '',
    };

    setEditingId(dependent.id);
    setValues(next);
    onValidate(next);
  };

  const stopEditing = () => {
    setEditingId(null);
    setValues(EMPTY_DEPENDENT_FORM);
    onValidate(EMPTY_DEPENDENT_FORM);
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setSaving(true);

    const done = editingId === null ? onAdd(values) : onUpdate(editingId, values);

    void done
      .then(() => {
        setValues(EMPTY_DEPENDENT_FORM);
        setEditingId(null);
      })
      .finally(() => {
        setSaving(false);
      });
  };

  const canSubmit =
    !submissionsClosed &&
    // The cap applies to adding another, not to correcting one that is counted
    // already.
    (canAddMore || editingId !== null) &&
    Object.keys(errors).length === 0 &&
    ineligibleReasonsAr.length === 0 &&
    values.relation !== '' &&
    values.fullName !== '' &&
    values.birthDate !== '';

  return (
    <div className={authStyles.page}>
      <h1 className="visually-hidden">المعالون</h1>

      <Card title="المعالون" description="من تطلب تغطيتهم من أسرتك">
        {errorAr ? <Alert severity="error">{errorAr}</Alert> : null}

        {submissionsClosed ? (
          <Alert severity="warning">فترة التقديم مغلقة حاليًا.</Alert>
        ) : null}

        {/* The roster said one thing; the employee has entered another. "I
            thought I finished" is the most common way this ends up incomplete. */}
        {childrenShortfall > 0 ? (
          <Alert severity="warning" title="ناقص">
            {`سجلّك يشير إلى ${String(childrenShortfall)} من الأبناء لم تُضفهم بعد.`}
          </Alert>
        ) : null}

        <div className={styles.list}>
          {dependents.length === 0 ? (
            <p className={styles.empty}>لم تُضف أي معال بعد.</p>
          ) : (
            dependents.map((dependent) => (
              <div key={dependent.id} className={styles.entry}>
                <div className={styles.entryDetails}>
                  <span className={styles.entryName}>{dependent.fullName}</span>
                  <span className={styles.entryMeta}>
                    {`${dependent.relationLabelAr} · ${formatDateAr(dependent.birthDate)}`}
                  </span>
                </div>
                <div className={styles.entryActions}>
                  <Badge tone={dependent.statusTone} withDot>
                    {dependent.statusLabelAr}
                  </Badge>

                  {/* Offered only while the rules still accept a change. Once
                      the committee approves this one, it locks — and only this
                      one. */}
                  {dependent.editable && !submissionsClosed ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        startEditing(dependent);
                      }}
                    >
                      تعديل
                    </Button>
                  ) : null}
                </div>
              </div>
            ))
          )}
        </div>
      </Card>

      <Card title={editingId === null ? 'إضافة معال' : 'تعديل بيانات معال'}>
        {!canAddMore && editingId === null ? (
          <Alert severity="info">بلغت الحد الأقصى لعدد المعالين المسموح به.</Alert>
        ) : null}

        <form onSubmit={handleSubmit} noValidate>
          <div className={authStyles.formGrid}>
            <SelectField
              label="صلة القرابة"
              required
              value={values.relation}
              onChange={set('relation')}
              error={errors.relation}
              disabled={(!canAddMore && editingId === null) || submissionsClosed}
            >
              <option value="" disabled>
                اختر…
              </option>
              {relationOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.labelAr}
                </option>
              ))}
            </SelectField>

            <TextField
              label={firstNameOnly ? 'الاسم الأول' : 'الاسم الكامل'}
              required
              value={values.fullName}
              onChange={set('fullName')}
              error={errors.fullName}
              hint={
                firstNameOnly && derivedChildNameAr
                  ? `سيُسجَّل: ${derivedChildNameAr}`
                  : undefined
              }
              disabled={(!canAddMore && editingId === null) || submissionsClosed}
            />

            {isChildRelation ? (
              <div className={authStyles.spanAll}>
                {/* Half-siblings from the mother: the lineage cannot be
                    derived from this employee, so the name is asked in full. */}
                <label className={styles.checkboxRow}>
                  <input
                    type="checkbox"
                    checked={values.differentFather}
                    onChange={(event) => {
                      setDifferentFather(event.target.checked);
                    }}
                    disabled={(!canAddMore && editingId === null) || submissionsClosed}
                  />
                  <span>الأب مختلف (إخوة من الأم)</span>
                </label>
              </div>
            ) : null}

            <SelectField
              label="الجنس"
              required
              value={values.gender}
              onChange={set('gender')}
              error={errors.gender}
              disabled={(!canAddMore && editingId === null) || submissionsClosed}
            >
              <option value="" disabled>
                اختر…
              </option>
              <option value="male">ذكر</option>
              <option value="female">أنثى</option>
            </SelectField>

            <DateField
              label="تاريخ الميلاد"
              required
              value={values.birthDate}
              onChange={(birthDate) => {
                const next = { ...values, birthDate };
                setValues(next);
                onValidate(next);
              }}
              error={errors.birthDate}
              disabled={(!canAddMore && editingId === null) || submissionsClosed}
            />

            <div className={authStyles.spanAll}>
              <TextField
                label="رقم الهوية"
                optional
                numeric
                inputMode="numeric"
                value={values.nationalId}
                onChange={set('nationalId')}
                error={errors.nationalId}
                hint="يُترك فارغًا إن لم تصدر الهوية بعد"
                disabled={(!canAddMore && editingId === null) || submissionsClosed}
              />
            </div>

            {/* A policy refusal, kept visually distinct from a field error: no
                amount of retyping changes what the fund covers. */}
            {ineligibleReasonsAr.length > 0 ? (
              <div className={authStyles.spanAll}>
                <Alert severity="warning" title="غير مشمول بالتغطية">
                  <div className={styles.ineligible}>
                    <ul className={styles.ineligibleList}>
                      {ineligibleReasonsAr.map((reason) => (
                        <li key={reason}>{reason}</li>
                      ))}
                    </ul>
                  </div>
                </Alert>
              </div>
            ) : null}

            {requiresProofDocument && ineligibleReasonsAr.length === 0 ? (
              <div className={authStyles.spanAll}>
                <Alert severity="info">
                  ستحتاج إرفاق مستند يثبت صلة القرابة بعد الإضافة.
                </Alert>
              </div>
            ) : null}

            <div className={authStyles.spanAll}>
              <div className={styles.formActions}>
                <Button type="submit" size="lg" loading={saving} disabled={!canSubmit}>
                  {editingId === null ? 'إضافة المعال' : 'حفظ التعديل'}
                </Button>

                {editingId === null ? null : (
                  <Button type="button" variant="ghost" size="lg" onClick={stopEditing}>
                    إلغاء
                  </Button>
                )}
              </div>
            </div>
          </div>
        </form>
      </Card>
    </div>
  );
}
